export interface PriceCalculator {
  originalPrice: number;
  basePrice: number;
  plays: number;
  price: number;
  addOnPrice?: number;
  originalAddOnPrice?: number;
}

/** A promotional DISCOUNT grant (#1689): a one-shot percentage or fixed amount off. */
export type PromoDiscountGrant = {
  kind: 'pct' | 'amount';
  pct: number | null;
  amount: number | null;
  expiresAt?: string | null;
};

/**
 * Dollar amount a promotional DISCOUNT grant (#1689) takes off `price` for a given
 * offer, mirroring the server (see MembershipOrder discount handling): the discount
 * applies BEFORE credit/balance, never on a subscription or on a flat win-back /
 * upgrade price, and never exceeds the price. Shared by the order page's PriceInfo
 * (final total), the option cards, and the promo banner so all three show the same
 * number and never drift.
 */
export function promoDiscountDollars(
  price: number,
  offer: API.MembershipProductOffer | undefined,
  promoDiscount: PromoDiscountGrant | null | undefined,
): number {
  if (!promoDiscount) {
    return 0;
  }
  const isSubscription = !!offer?.time?.subscription?.interval;
  const hasFlatPrice =
    (!!offer?.winback && typeof offer.winback.price === 'number') ||
    (!!offer?.upgrade && typeof offer.upgrade.price === 'number');
  if (isSubscription || hasFlatPrice) {
    return 0;
  }
  return promoDiscount.kind === 'pct'
    ? Math.min(price, Math.round(price * (promoDiscount.pct || 0) * 100) / 100)
    : Math.min(price, Math.max(0, promoDiscount.amount || 0));
}

export default function calculatePrice({
  discount,
  stats,
  variety,
}: {
  discount: number;
  stats: API.MembershipProductStats;
  variety: API.MembershipProductOffer;
  // relatedProducts?: API.MembershipProduct[];
}): PriceCalculator {
  // For season passes, prorate against estimatedPlays (not the raw plays field).
  // For play packs, prorate against plays as before.
  const denominator =
    variety.type === 'season' ? (variety.estimatedPlays ?? variety.plays) : variety.plays;
  const plays =
    variety.auto && denominator - stats.played > 0 ? denominator - stats.played : denominator;
  const startPrice = variety.price || 0;
  const originalPrice = Math.ceil(
    variety.auto ? (((startPrice * 100) / denominator) * plays) / 100 : startPrice,
  );
  let addOnPrice = 0;
  if (variety?.addOn?.price) {
    addOnPrice = Math.ceil(variety?.addOn?.price * (1 - discount) || 0);
  }
  const originalAddOnPrice = variety?.addOn?.price || 0;

  // Per-user flat prices the server stamped onto this offer for a targeted account.
  // Each is the FINAL base price — bypass auto-proration and the global percentage
  // discount so the page shows exactly what checkout will charge; `originalPrice`
  // still holds the catalog price for the strikethrough.
  //   * winback  — NFL win-back price (#1313)
  //   * upgrade  — CFB Weeks 0-1 intro -> full season (#1491): season price minus
  //                what the buyer already paid for their intro.
  // They are mutually exclusive (different products); win-back wins if both appear.
  const winbackPrice = variety?.winback?.price;
  const upgradePrice = variety?.upgrade?.price;
  const flatPrice = winbackPrice ?? upgradePrice;
  let price =
    flatPrice !== undefined && flatPrice !== null
      ? Math.ceil(flatPrice)
      : Math.ceil(originalPrice * (1 - discount));

  const basePrice = price;

  // @todo need to look up all products and check if it has auto on the main item
  // We should probably split this off into something on the API and let it get cached
  if (variety.combos) {
    for (const combo of variety.combos) {
      price += Math.ceil((combo.price || 0) * (1 - discount));
    }
  }

  if (variety.addOn) {
    price += Math.ceil((variety.addOn.price || 0) * (1 - discount));
  }

  return { originalPrice, basePrice, addOnPrice, originalAddOnPrice, plays, price };
}
